import { Pencil, Trash2 } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { apriFile } from '../../services/fileUrl';
import FileMedia from '../FileMedia';
import { fotoAPI } from '../../services/api';
import '../../styles/Foto.css';
import { annunciaFotoCambiate } from '../../utils/annunci';
import { notificaErrore, messaggioDa } from '../../utils/notifiche';
import { accettaDi } from '../../utils/tipiFile';

const ETICHETTE = ['Before', 'After', 'Controllo', 'Altro'];

/* CHE COSA OFFRE IL SELETTORE (6/10/2026): i formati che il server riconosce
   dai byte (backend/utils/tipoFile.js, copiato in utils/tipiFile.js a ogni
   compilazione), delle famiglie che la galleria accetta. Fino ad allora era
   `image/*,video/*,application/pdf`: «image/*» offre anche il disegno
   vettoriale (SVG), che il server respinge apposta. */
const ACCETTA_GALLERIA = accettaDi(['image/', 'video/', 'application/pdf']);

const FotoTab = ({ pazienteId }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [etichetta, setEtichetta] = useState('Before');
    const [uploading, setUploading] = useState(false);
    const [filtro, setFiltro] = useState('tutte');
    const inputRef = useRef();

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [pazienteId]);

    const load = async () => {
        try {
            setLoading(true);
            const r = await fotoAPI.getByPaziente(pazienteId);
            if (r.data.success) setItems(r.data.data.foto || []);
        } catch (e) { console.error('Errore caricamento file', e); }
        finally { setLoading(false); }
    };

    const scegliFile = () => inputRef.current && inputRef.current.click();

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        try {
            for (const f of files) {
                const fd = new FormData();
                fd.append('file', f);
                fd.append('etichetta', etichetta);
                await fotoAPI.upload(pazienteId, fd);
            }
            load(); annunciaFotoCambiate();
        } catch (err) {
            /* Il server dice gia' che cosa non va - troppo grande, tipo
               non ammesso - e lo dice in italiano. Il messaggio di prima
               tirava a indovinare con un punto interrogativo, e chi
               leggeva non sapeva quale dei due fosse ne' quale file. */
            notificaErrore(messaggioDa(err, 'Non riesco a caricare il file.'));
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const elimina = async (id) => {
        /* Il messaggio diceva che la cancellazione era definitiva, ed era
           vero: cancellava la riga e il file. Dall'11 settembre 2026 la
           cancellazione e' logica - il record resta e il file si sposta
           in un cestino - perche' una fotografia clinica non si butta via
           con un clic. Dirlo com'e' evita sia il clic troppo facile sia
           la paura di premere. */
        if (!window.confirm('Eliminare questo file?\n\nSparisce dalla scheda del paziente. '
                          + 'Resta recuperabile da chi amministra il sistema.')) return;
        try { await fotoAPI.remove(id); load(); annunciaFotoCambiate(); }
        catch (e) { notificaErrore(messaggioDa(e, 'Non riesco a eliminare la fotografia')); }
    };

    const etichettePresenti = [...new Set(items.map(i => i.etichetta).filter(Boolean))];
    const visibili = filtro === 'tutte'
        ? items
        : items.filter(i => (i.etichetta || '') === filtro);

    return (
        <div className="ft-wrap">
            <div className="ft-toolbar">
                <label className="ft-tool-label">Etichetta:</label>
                <select value={etichetta} onChange={e => setEtichetta(e.target.value)} className="ft-select">
                    {ETICHETTE.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
                <button className="ft-upload-btn" onClick={scegliFile} disabled={uploading}>
                    {uploading ? 'Caricamento…' : '+ Carica foto/video/documento'}
                </button>
                <input ref={inputRef} type="file" accept={ACCETTA_GALLERIA}
                       multiple style={{ display: 'none' }} onChange={onFiles} />
            </div>

            {etichettePresenti.length > 0 && (
                <div className="ft-filtri">
                    <button className={filtro === 'tutte' ? 'active' : ''} onClick={() => setFiltro('tutte')}>Tutte</button>
                    {etichettePresenti.map(x => (
                        <button key={x} className={filtro === x ? 'active' : ''} onClick={() => setFiltro(x)}>{x}</button>
                    ))}
                </div>
            )}

            {loading ? <div className="sp-loading">Caricamento…</div> : (
                visibili.length === 0 ? (
                    <p className="ft-empty">Nessun file caricato.</p>
                ) : (
                    <div className="ft-grid">
                        {visibili.map(it => (
                            <div key={it.id} className="ft-card">
                                <div className="ft-media">
                                    {it.categoria === 'foto' && <FileMedia url={it.url} tipo="img" alt={it.nome_file} />}
                                    {it.categoria === 'video' && <FileMedia url={it.url} tipo="video" />}
                                    {it.categoria === 'documento' && (
                                        <button type="button" onClick={() => apriFile(it.url)} className="ft-doc">
                                            <span className="ft-doc-ico"></span>
                                            <span className="ft-doc-name">{it.nome_file}</span>
                                        </button>
                                    )}
                                </div>
                                <div className="ft-meta">
                                    {it.etichetta && <span className="ft-badge">{it.etichetta}</span>}
                                    <button className="ft-open" type="button" onClick={() => apriFile(it.url)}>Apri</button>
                                    <button className="ft-del" onClick={() => elimina(it.id)} title="Elimina"><Trash2 size={15} strokeWidth={1.8} aria-hidden="true" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            )}
        </div>
    );
};

export default FotoTab;
